import { collection, doc, getDoc, getDocs, runTransaction, serverTimestamp } from "firebase/firestore";

// Keep this module beside DriverManagerDrivers.js. All future asset writers must
// use these operations before switching an existing company to paged summaries.
export const VEHICLE_SUMMARY_LIMIT = Math.floor(1048576 * 0.7);
const SCHEMA_VERSION = 1;
const text = (value) => String(value ?? "").trim();
const isRecord = (value) => value && typeof value === "object" && !Array.isArray(value);
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const utf8 = (value) => new TextEncoder().encode(String(value)).length;
const vehicleTypes = new Set(["INTEGRATED_VEHICLE", "GENERAL_TRUCK", "TRACTOR", "OIL_TANKER", "CHEMICAL_TANKER", "SEPTIC_TANKER", "WATER_TANKER", "SPRINKLER_TRUCK", "REFRIGERATED_TRUCK", "DUMP_TRUCK", "CONCRETE_MIXER"]);
const trailerTypes = new Set(["OIL_TANK_TRAILER", "CHEMICAL_TANK_TRAILER", "REFRIGERATED_TRAILER", "FLATBED_TRAILER", "SEMI_TRAILER"]);
const containerTypes = new Set(["FUEL_TANK", "CHEMICAL_TANK", "WATER_TANK", "WASTE_TANK", "CARGO_CONTAINER", "IBC_CONTAINER"]);

function requireId(value, label) {
  if (typeof value !== "string" || !value.trim() || value.includes("/") || value === "." || value === "..") throw new Error(`${label} 無效。`);
  return value;
}
export function vehicleSummaryPageId(number) {
  if (!Number.isSafeInteger(number) || number < 1) throw new Error("摘要頁碼無效。");
  return String(number).padStart(4, "0");
}
function validPageId(id) {
  return typeof id === "string" && /^[0-9]{4,}$/.test(id) && vehicleSummaryPageId(Number(id)) === id;
}
function normalizeAssetStatus(value) {
  const status = text(value).toUpperCase();
  if (["PARKED", "IN_USE", "MAINTENANCE", "RETIRED"].includes(status)) return status;
  if (["ATTACHED", "INUSE", "ASSIGNED"].includes(status)) return "IN_USE";
  return "PARKED";
}
function kindOf(asset) {
  const kind = text(asset.assetKind).toLowerCase();
  if (["vehicle", "trailer", "container", "equipment"].includes(kind)) return kind;
  const type = text(asset.assetType).toUpperCase();
  if (vehicleTypes.has(type)) return "vehicle";
  if (trailerTypes.has(type)) return "trailer";
  if (containerTypes.has(type)) return "container";
  return "equipment"; // Unknown assets must never appear as selectable vehicles.
}
function bindingIds(ids) {
  if (ids == null) return [];
  if (!Array.isArray(ids)) throw new Error("掛載清單格式錯誤。");
  return [...new Set(ids.map(id => requireId(id, "掛載資產 ID")))].sort();
}
export function vehicleBindingVersion(data) {
  return JSON.stringify([bindingIds(data?.trailerIds), bindingIds(data?.containerIds),
    data?.updatedAt?.seconds ?? null, data?.updatedAt?.nanoseconds ?? null, data?.revision ?? 0]);
}
// Persist only list identity fields. The enclosing items map owns the asset ID.
export function vehicleSummaryItem(asset, assetId) {
  requireId(assetId, "資產 ID");
  const assetKind = kindOf(asset);
  const storedStatus = normalizeAssetStatus(asset.status);
  // vehicles.status is authoritative; driver pointers are indexes, not status derivations.
  const status = storedStatus;
  const summary = {
    assetKind,
    assetType: text(asset.assetType).toUpperCase(),
    status,
  };
  for (const field of ["plateNumber", "displayName", "make", "model", "assetTypeOther", "currentDriverId", "currentDriverName", "currentVehicleId"]) {
    const value = text(asset[field]);
    if (value) summary[field] = value;
  }
  const parked = isRecord(asset.parkedLocation) ? asset.parkedLocation : {};
  if (text(parked.locationId)) summary.parkedLocationId = text(parked.locationId);
  if (text(parked.name)) summary.parkedLocationName = text(parked.name);
  if (summary.assetKind === "vehicle") {
    summary.trailerIds = bindingIds(asset.trailerIds);
    summary.containerIds = bindingIds(asset.containerIds);
  }
  return summary;
}

// Firestore-style size estimate for the deliberately restricted summary schema.
// Includes UTF-8 field names, document path and map overhead; an additional
// 4096-byte margin covers server-generated timestamps/metadata and estimation drift.
// This is NOT a general Firestore value serializer (references/blobs are rejected).
function valueBytes(value) {
  if (value === null || typeof value === "boolean") return 1;
  if (typeof value === "number") return 8;
  if (typeof value === "string") return utf8(value) + 1;
  if (Array.isArray(value)) return value.reduce((n, item) => n + valueBytes(item), 0);
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return 32 + Object.entries(value).reduce((n, [key, item]) => n + utf8(key) + 1 + valueBytes(item), 0);
  }
  throw new Error("摘要包含不支援的資料型別。");
}
export function estimateVehicleSummaryBytes(data, documentPath) {
  const nameBytes = documentPath.split("/").reduce((n, part) => n + utf8(part) + 1, 16);
  return nameBytes + valueBytes(data) + 4096;
}
function pageData(orgId, pageId, items, nextPageId = null) {
  const data = { schemaVersion: SCHEMA_VERSION, pageNo: Number(pageId), nextPageId,
    itemCount: Object.keys(items).length, estimatedBytes: 0, items };
  data.estimatedBytes = estimateVehicleSummaryBytes(data, `orgs/${orgId}/vehicleSummary/${pageId}`);
  return data;
}
function readPage(snapshot, id) {
  if (!snapshot.exists()) throw new Error(`找不到車輛摘要頁 ${id}，請由管理端修復摘要。`);
  const data = snapshot.data();
  if (data.schemaVersion !== SCHEMA_VERSION || data.pageNo !== Number(id) || !isRecord(data.items) ||
      !(data.nextPageId === null || validPageId(data.nextPageId))) throw new Error(`車輛摘要頁 ${id} 格式不正確。`);
  return data;
}

// No vehicles collection query, automatic backfill, or writes on the read path.
// A paged company needs exactly one document read when 0001.nextPageId is null.
// An unmigrated company needs two: missing 0001, then the existing legacy list.
export async function readVehicleDirectory(db, orgId) {
  requireId(orgId, "公司 ID");
  const first = await getDoc(doc(db, "orgs", orgId, "vehicleSummary", "0001"));
  if (!first.exists()) {
    const legacy = await getDoc(doc(db, "orgs", orgId, "vehicleSummary", "list"));
    if (!legacy.exists()) throw new Error("尚未建立車輛摘要，請先到車輛管理頁載入資產。");
    const data = legacy.data();
    if (!isRecord(data.items)) throw new Error("舊版車輛摘要格式不正確。");
    return { source: "legacy", pagesRead: 2,
      items: Object.entries(data.items).map(([id, asset]) => ({ ...vehicleSummaryItem(asset, id), assetId: id })) };
  }
  let id = "0001", snapshot = first;
  const visited = new Set(), seenAssets = new Set(), items = [];
  while (true) {
    if (visited.has(id)) throw new Error("車輛摘要分頁連結重複，請修復摘要。");
    visited.add(id);
    const data = readPage(snapshot, id);
    for (const [assetId, asset] of Object.entries(data.items)) {
      if (seenAssets.has(assetId)) throw new Error("車輛摘要有重複資產，請修復摘要。");
      seenAssets.add(assetId);
      items.push({ ...vehicleSummaryItem(asset, assetId), assetId, summaryPageId: id });
    }
    if (data.nextPageId === null) break;
    id = data.nextPageId;
    snapshot = await getDoc(doc(db, "orgs", orgId, "vehicleSummary", id));
  }
  return { source: "paged", pagesRead: visited.size, items };
}

export function selectableVehicleSummaries(items) {
  return items.filter((item) => item.assetKind === "vehicle" && item.status === "PARKED")
    .map((item) => ({ ...item, id: item.assetId }))
    .sort((a, b) => (a.plateNumber || a.displayName || a.assetId || a.id)
      .localeCompare(b.plateNumber || b.displayName || b.assetId || b.id, "zh-Hant"));
}

// Readers never migrate. Cars calls this once before enabling asset mutations.
// Switch all deployed writers to this module; an old application tab must be
// reloaded before migration, since frontend code cannot revoke an old writer.
export async function ensureVehicleDirectory(db, orgId, actorUid) {
  requireId(orgId, "公司 ID"); requireId(actorUid, "操作者 ID");
  const firstRef = doc(db, "orgs", orgId, "vehicleSummary", "0001");
  const legacyRef = doc(db, "orgs", orgId, "vehicleSummary", "list");
  const metaRef = doc(db, "orgs", orgId, "vehicleSummary", "directoryMeta");
  const first = await getDoc(firstRef);
  if (first.exists()) {
    await runVehicleDirectoryTransaction(db, orgId, actorUid, async (tx) => {
      tx.setSummary(tx.getSummary());
    });
    return readVehicleDirectory(db, orgId);
  }
  const legacy = await getDoc(legacyRef);
  // Only when no summary exists, bootstrap from complete masters once.
  const initial = legacy.exists() ? null : await getDocs(collection(db, "orgs", orgId, "vehicles"));
  await runTransaction(db, async (tx) => {
    const liveFirst = await tx.get(firstRef);
    const liveLegacy = await tx.get(legacyRef);
    const liveMeta = await tx.get(metaRef);
    if (liveFirst.exists()) return;
    if (liveMeta.exists()) throw new Error("摘要管理文件存在但第一頁遺失，請先修復，避免覆蓋既有分頁。");
    let items;
    if (liveLegacy.exists()) {
      if (!isRecord(liveLegacy.data().items)) throw new Error("舊摘要格式不正確，未執行轉換。");
      items = liveLegacy.data().items;
    } else {
      if (!initial) throw new Error("摘要在轉換期間變動，請重新整理。");
      items = {};
      for (const row of initial.docs) {
        const fresh = await tx.get(row.ref);
        if (fresh.exists()) items[row.id] = fresh.data();
      }
    }
    for (const [id, item] of Object.entries(items)) {
      if (kindOf(item) !== "vehicle") continue;
      const bound = await tx.get(doc(db, "orgs", orgId, "vehicleBindings", id));
      const relation = bound.exists() ? bound.data() : {};
      items[id] = { ...item, trailerIds: bindingIds(relation.trailerIds), containerIds: bindingIds(relation.containerIds) };
    }
    const pages = packVehicleSummaryPages(orgId, items);
    for (const page of pages.slice(1)) {
      if ((await tx.get(doc(db, "orgs", orgId, "vehicleSummary", vehicleSummaryPageId(page.pageNo)))).exists()) {
        throw new Error("已有分頁但第一頁遺失，未覆蓋原資料。");
      }
    }
    // Atomic cutover: no half-built page chain and no merge of obsolete fields.
    for (const page of pages) tx.set(doc(db, "orgs", orgId, "vehicleSummary", vehicleSummaryPageId(page.pageNo)),
      { ...page, updatedByUid: actorUid, updatedAt: serverTimestamp() });
    tx.set(metaRef, { schemaVersion: SCHEMA_VERSION, latestPageId: vehicleSummaryPageId(pages.length),
      pageCount: pages.length, totalItems: Object.keys(items).length, revision: 1,
      updatedByUid: actorUid, updatedAt: serverTimestamp() });
    if (liveLegacy.exists()) tx.delete(legacyRef);
  });
  return readVehicleDirectory(db, orgId);
}

export function packVehicleSummaryPages(orgId, sourceItems) {
  const pages = [];
  let number = 1, items = {};
  for (const [id, source] of Object.entries(sourceItems)) {
    const summary = vehicleSummaryItem(source, id);
    const candidate = pageData(orgId, vehicleSummaryPageId(number), { ...items, [id]: summary }, vehicleSummaryPageId(number + 1));
    if (candidate.estimatedBytes > VEHICLE_SUMMARY_LIMIT) {
      if (!Object.keys(items).length) throw new Error("單筆摘要過大，請縮短名稱等欄位。");
      pages.push(pageData(orgId, vehicleSummaryPageId(number), items, vehicleSummaryPageId(number + 1)));
      number += 1; items = {};
      if (pageData(orgId, vehicleSummaryPageId(number), { [id]: summary }, vehicleSummaryPageId(number + 1)).estimatedBytes > VEHICLE_SUMMARY_LIMIT) {
        throw new Error("單筆摘要過大，請縮短名稱等欄位。");
      }
    }
    items[id] = summary;
  }
  pages.push(pageData(orgId, vehicleSummaryPageId(number), items));
  return pages;
}

// Atomic shared transaction: reads summary pages once, exposes a virtual map to
// business code, sanitizes it, and writes ONLY changed pages. Existing items stay
// on their original page; new/oversized edits append at the tail. Empty pages remain.
export async function runVehicleDirectoryTransaction(db, orgId, actorUid, callback) {
  requireId(orgId, "公司 ID"); requireId(actorUid, "操作者 ID");
  const ref = (id) => doc(db, "orgs", orgId, "vehicleSummary", id);
  return runTransaction(db, async (tx) => {
    const metaSnap = await tx.get(ref("directoryMeta"));
    const legacySnap = await tx.get(ref("list"));
    if (legacySnap.exists()) throw new Error("偵測到舊版程式仍在寫入摘要，請關閉舊分頁並重新整理。");
    if (!metaSnap.exists()) throw new Error("請先開啟車輛管理完成摘要轉換。");
    const meta = metaSnap.data(), pages = new Map(), original = {}, pageOf = new Map();
    let id = "0001";
    while (id) {
      if (pages.has(id)) throw new Error("摘要分頁連結重複。");
      const data = readPage(await tx.get(ref(id)), id);
      pages.set(id, data);
      for (const [assetId, item] of Object.entries(data.items)) {
        if (own(original, assetId)) throw new Error("摘要出現重複資產。");
        original[assetId] = item; pageOf.set(assetId, id);
      }
      id = data.nextPageId;
    }
    let tailId = [...pages.keys()].at(-1);
    if (meta.schemaVersion !== SCHEMA_VERSION || meta.pageCount !== pages.size || meta.latestPageId !== tailId || meta.totalItems !== Object.keys(original).length) throw new Error("摘要管理資訊不一致，請先修復。");
    let requested = null;
    const bindingUpdates = new Map();
    const queued = [];
    const result = await callback({
      get: (reference) => tx.get(reference),
      getSummary: () => Object.fromEntries(Object.entries(original).map(([key, value]) => [key, { ...value }])),
      setSummary: (items) => { requested = items; },
      setVehicleBindingSummary: (id, value) => { bindingUpdates.set(id, value); },
      set: (...args) => queued.push(["set", args]),
      delete: (...args) => queued.push(["delete", args]),
      update: (...args) => queued.push(["update", args]),
    });
    const changed = new Set();
    const working = new Map([...pages].map(([key, value]) => [key, { ...value, items: { ...value.items } }]));
    if (requested !== null) {
      if (!isRecord(requested)) throw new Error("摘要必須是資產 map。");
      const next = {};
      for (const [key, value] of Object.entries(requested)) {
        let item = value;
        if (kindOf(value) !== "vehicle" && ((original[key]?.trailerIds?.length || 0) + (original[key]?.containerIds?.length || 0) > 0)) {
          throw new Error("車輛仍有掛載關係，請先解除後再變更資產種類。");
        }
        if (kindOf(value) === "vehicle") {
          let binding = bindingUpdates.get(key);
          if (!binding) {
            const old = original[key];
            if (old && Array.isArray(old.trailerIds) && Array.isArray(old.containerIds)) binding = old;
            else {
              const snapshot = await tx.get(doc(db, "orgs", orgId, "vehicleBindings", key));
              binding = snapshot.exists() ? snapshot.data() : {};
            }
          }
          item = { ...value, trailerIds: bindingIds(binding.trailerIds), containerIds: bindingIds(binding.containerIds) };
        }
        next[key] = vehicleSummaryItem(item, key);
      }
      for (const key of Object.keys(original)) {
        if (!own(next, key)) { const pageId = pageOf.get(key); delete working.get(pageId).items[key]; changed.add(pageId); pageOf.delete(key); }
      }
      for (const [key, value] of Object.entries(next)) {
        if (own(original, key) && JSON.stringify(original[key]) === JSON.stringify(value)) continue;
        let targetId = pageOf.get(key) || tailId;
        let target = working.get(targetId);
        const candidate = { ...target.items, [key]: value };
        // Reserve a next-page pointer even on the last page.
        if (pageData(orgId, targetId, candidate, target.nextPageId || vehicleSummaryPageId(Number(targetId) + 1)).estimatedBytes > VEHICLE_SUMMARY_LIMIT) {
          if (pageOf.has(key)) { delete target.items[key]; changed.add(targetId); }
          targetId = tailId; target = working.get(tailId);
          if (pageData(orgId, tailId, { ...target.items, [key]: value }, vehicleSummaryPageId(Number(tailId) + 1)).estimatedBytes > VEHICLE_SUMMARY_LIMIT) {
            const newId = vehicleSummaryPageId(Number(tailId) + 1);
            if ((await tx.get(ref(newId))).exists()) throw new Error("下一摘要頁已存在，請先修復。");
            if (pageData(orgId, newId, { [key]: value }, vehicleSummaryPageId(Number(newId) + 1)).estimatedBytes > VEHICLE_SUMMARY_LIMIT) throw new Error("單筆摘要過大。");
            target.nextPageId = newId; changed.add(tailId);
            targetId = newId; tailId = newId;
            target = { items: {}, nextPageId: null };
            working.set(newId, target);
          }
        }
        target.items[key] = value; changed.add(targetId); pageOf.set(key, targetId);
      }
    }
    // All reads precede actual writes. Caller writes and summary commit together.
    for (const [operation, args] of queued) tx[operation](...args);
    for (const pageId of changed) {
      const page = working.get(pageId);
      const data = pageData(orgId, pageId, page.items, page.nextPageId);
      if (data.estimatedBytes > VEHICLE_SUMMARY_LIMIT) throw new Error("摘要超過容量門檻。");
      tx.set(ref(pageId), { ...data, updatedByUid: actorUid, updatedAt: serverTimestamp() });
    }
    if (requested !== null && changed.size) tx.set(ref("directoryMeta"), { schemaVersion: SCHEMA_VERSION,
      latestPageId: tailId, pageCount: working.size, totalItems: Object.keys(requested).length,
      revision: (meta.revision || 0) + 1, updatedByUid: actorUid, updatedAt: serverTimestamp() });
    return result;
  });
}

// Complete-master API for callers that use optimistic revisions. Cars currently
// uses the transaction API above so its state writes are in the same commit.
export async function saveVehicleAsset(db, { orgId, assetId, asset, actorUid, expectedRevision }) {
  requireId(assetId, "資產 ID");
  if (!isRecord(asset)) throw new Error("請提供完整資產資料。");
  return runVehicleDirectoryTransaction(db, orgId, actorUid, async (tx) => {
    const reference = doc(db, "orgs", orgId, "vehicles", assetId);
    const snapshot = await tx.get(reference), current = snapshot.data();
    if (snapshot.exists() && expectedRevision !== (current.revision || 0)) throw new Error("資產已更新，請重新載入。");
    if (!snapshot.exists() && expectedRevision != null) throw new Error("資產已不存在。");
    const status = text(asset.status).toUpperCase();
    if (!["PARKED", "IN_USE", "MAINTENANCE", "RETIRED"].includes(status)) throw new Error("資產狀態只允許停放中、使用中、維修中或已報廢。");
    const relationSnapshot = snapshot.exists() && kindOf(current) === "vehicle"
      ? await tx.get(doc(db, "orgs", orgId, "vehicleBindings", assetId)) : null;
    const relation = relationSnapshot?.exists() ? relationSnapshot.data() : {};
    const hasRelations = Boolean(current?.currentDriverId || current?.currentVehicleId ||
      (relation.trailerIds || []).length || (relation.containerIds || []).length);
    if (snapshot.exists() && hasRelations && ((normalizeAssetStatus(current.status) === "IN_USE" && status !== "IN_USE") || ["MAINTENANCE", "RETIRED"].includes(status))) {
      throw new Error("資產仍有成員指派或掛載關係，請先解除後再更改狀態。");
    }
    const normalizedAsset = { ...asset, status };
    const items = tx.getSummary();
    items[assetId] = vehicleSummaryItem(normalizedAsset, assetId);
    tx.set(reference, { ...normalizedAsset, revision: (current?.revision || 0) + 1,
      createdAt: current?.createdAt || serverTimestamp(), updatedAt: serverTimestamp(), updatedByUid: actorUid });
    tx.setSummary(items);
    return { changed: true };
  });
}
export async function deleteVehicleAsset(db, { orgId, assetId, actorUid, expectedRevision }) {
  requireId(assetId, "資產 ID");
  // Caller enforces no active driver/asset/job relationships before deletion.
  return runVehicleDirectoryTransaction(db, orgId, actorUid, async (tx) => {
    const reference = doc(db, "orgs", orgId, "vehicles", assetId);
    const snapshot = await tx.get(reference);
    if (!snapshot.exists()) return { changed: false };
    if (expectedRevision !== (snapshot.data().revision || 0)) throw new Error("資產已更新，請重新載入。");
    const items = tx.getSummary(); delete items[assetId];
    tx.delete(reference); tx.setSummary(items);
    return { changed: true };
  });
}

// Detach one mounted trailer/container and transition it as a single atomic operation.
export async function detachAndUpdateAttachedAsset(db, { orgId, vehicleId, assetId, actorUid, nextStatus, parkedLocation, locationRef, locationData }) {
  requireId(orgId, "公司 ID"); requireId(vehicleId, "車輛 ID"); requireId(assetId, "資產 ID"); requireId(actorUid, "操作者 ID");
  if (!["PARKED", "MAINTENANCE", "RETIRED"].includes(nextStatus)) throw new Error("解除掛載後的資產狀態無效。");
  if (nextStatus === "PARKED" && parkedLocation && (!isRecord(parkedLocation) || !text(parkedLocation.locationId) || !text(parkedLocation.name) ||
      !Number.isFinite(Number(parkedLocation.lat)) || !Number.isFinite(Number(parkedLocation.lng)) || !locationRef || !isRecord(locationData))) {
    throw new Error("請選擇或設定有效的停放位置。");
  }
  const vehicleRef = doc(db, "orgs", orgId, "vehicles", vehicleId);
  const assetRef = doc(db, "orgs", orgId, "vehicles", assetId);
  const bindingRef = doc(db, "orgs", orgId, "vehicleBindings", vehicleId);
  const eventRef = doc(collection(db, "orgs", orgId, "fleetEvents"));
  return runVehicleDirectoryTransaction(db, orgId, actorUid, async (tx) => {
    const actorSnapshot = await tx.get(doc(db, "drivers", actorUid));
    const actor = actorSnapshot.data();
    const actorRole = text(actor?.role || actor?.memberRole || actor?.orgRole).toLowerCase();
    const actorName = text(actor?.displayName || actor?.name || actor?.fullName || actor?.legalName || actor?.profile?.displayName || actor?.profile?.name) || "";
    if (!actorSnapshot.exists() || actor?.orgId !== orgId || !["owner", "primary", "primary_admin", "deputy", "deputy_admin"].includes(actorRole)) {
      throw new Error("只有同公司的管理員可以處理掛載資產。");
    }
    const [vehicleSnapshot, bindingSnapshot, assetSnapshot] = await Promise.all([tx.get(vehicleRef), tx.get(bindingRef), tx.get(assetRef)]);
    if (!vehicleSnapshot.exists() || kindOf(vehicleSnapshot.data()) !== "vehicle") throw new Error("主車輛不存在，請重新整理。");
    if (!bindingSnapshot.exists() || !assetSnapshot.exists()) throw new Error("掛載關係或資產已變更，請重新整理後再操作。");
    const vehicle = vehicleSnapshot.data() || {};
    const binding = bindingSnapshot.data() || {};
    const asset = assetSnapshot.data() || {};
    const previousBinding = { trailerIds: bindingIds(binding.trailerIds), containerIds: bindingIds(binding.containerIds) };
    const isTrailer = previousBinding.trailerIds.includes(assetId);
    const isContainer = previousBinding.containerIds.includes(assetId);
    if (isTrailer === isContainer || (isTrailer ? kindOf(asset) !== "trailer" : kindOf(asset) !== "container")) {
      throw new Error("此資產已不在目前車輛的掛載清單中，請重新整理。");
    }
    if (asset.currentVehicleId && asset.currentVehicleId !== vehicleId) throw new Error("此資產已掛載到其他車輛，沒有修改任何資料。");
    if (asset.currentDriverId) throw new Error("此資產仍有成員指派，請先解除指派後再操作。");
    const nextBinding = {
      trailerIds: previousBinding.trailerIds.filter((id) => id !== assetId),
      containerIds: previousBinding.containerIds.filter((id) => id !== assetId),
    };
        const oldVehicleStatus = normalizeAssetStatus(vehicle.status);
    const vehicleStatus = oldVehicleStatus;
    const items = tx.getSummary();
    if (!items[vehicleId] || !items[assetId]) throw new Error("資產摘要不完整，未修改任何資料；請重新載入資產清單。");
    const now = serverTimestamp();
    tx.set(bindingRef, { ...nextBinding, revision: (Number(binding.revision) || 0) + 1, updatedAt: now, updatedByUid: actorUid }, { merge: true });
    tx.set(vehicleRef, { status: vehicleStatus, updatedAt: now, updatedByUid: actorUid }, { merge: true });
    tx.set(assetRef, { currentVehicleId: null, status: nextStatus, parkedLocation: nextStatus === "PARKED" ? (parkedLocation || null) : null,
      locationSource: nextStatus === "PARKED" && parkedLocation ? "MAP_PICK" : null,
      locationUpdatedByUid: nextStatus === "PARKED" && parkedLocation ? actorUid : null,
      locationUpdatedAt: nextStatus === "PARKED" && parkedLocation ? now : null,
      updatedAt: now, updatedByUid: actorUid }, { merge: true });
    if (nextStatus === "PARKED" && parkedLocation) tx.set(locationRef, locationData, { merge: true });
    items[vehicleId] = { ...items[vehicleId], status: vehicleStatus, trailerIds: nextBinding.trailerIds, containerIds: nextBinding.containerIds };
    items[assetId] = { ...items[assetId], status: nextStatus, currentVehicleId: "", parkedLocation: nextStatus === "PARKED" ? (parkedLocation || null) : null };
    tx.setVehicleBindingSummary(vehicleId, nextBinding);
    tx.setSummary(items);
    const eventType = nextStatus === "PARKED" ? "ASSET_DETACHED_AND_PARKED" : nextStatus === "MAINTENANCE" ? "ASSET_DETACHED_FOR_MAINTENANCE" : "ASSET_DETACHED_AND_RETIRED";
    tx.set(eventRef, { type: eventType, eventType, vehicleId, assetId, assetIds: [vehicleId, assetId],
      previousStatus: normalizeAssetStatus(asset.status), status: nextStatus, previousBinding, nextBinding,
      ...(nextStatus === "PARKED" && parkedLocation ? { locationId: parkedLocation.locationId, locationLabel: parkedLocation.name, lat: Number(parkedLocation.lat), lng: Number(parkedLocation.lng), startedAt: parkedLocation.startedAt, locationSource: "MAP_PICK" } : {}),
      actorUid, actorName, actorRole, createdAt: now });
    return { changed: true, vehicleStatus, status: nextStatus, binding: nextBinding };
  });
}

// Shared by administrator and driver callers. Firestore rules must enforce the same permissions.
export async function updateVehicleAssetBindings(db, { orgId, vehicleId, trailerIds, containerIds, actorUid, expectedVersion }) {
  requireId(vehicleId, "車輛 ID"); requireId(orgId, "公司 ID"); requireId(actorUid, "操作者 ID");
  const nextBinding = { trailerIds: bindingIds(trailerIds), containerIds: bindingIds(containerIds) };
  const desired = [...nextBinding.trailerIds, ...nextBinding.containerIds];
  if (new Set(desired).size !== desired.length || desired.includes(vehicleId)) throw new Error("同一資產不能重複掛載或綁定自己。");
  const eventRef = doc(collection(db, "orgs", orgId, "fleetEvents"));
  return runVehicleDirectoryTransaction(db, orgId, actorUid, async tx => {
    const actorSnapshot = await tx.get(doc(db, "drivers", actorUid));
    const actor = actorSnapshot.data();
    const actorRole = text(actor?.role || actor?.memberRole || actor?.orgRole).toLowerCase();
    const actorName = text(actor?.displayName || actor?.name || actor?.fullName || actor?.legalName || actor?.profile?.displayName || actor?.profile?.name) || "";
    if (!actorSnapshot.exists() || actor.orgId !== orgId) throw new Error("無權操作此公司的車輛。");
    if (!["owner", "primary", "primary_admin", "deputy", "deputy_admin"].includes(actorRole)) {
      if (actorRole !== "driver") throw new Error("無權管理車輛掛載。");
      if (text(actor.currentVehicleId) !== vehicleId) throw new Error("只能操作目前指派給自己的車輛。");
    }
    const vehicleRef = doc(db, "orgs", orgId, "vehicles", vehicleId);
    const vehicleSnapshot = await tx.get(vehicleRef);
    if (!vehicleSnapshot.exists() || kindOf(vehicleSnapshot.data()) !== "vehicle") throw new Error("車輛不存在。");
    const vehicle = vehicleSnapshot.data();
    const vehicleStatus = normalizeAssetStatus(vehicle.status);
    if (desired.length && !["PARKED", "IN_USE"].includes(vehicleStatus)) throw new Error("維修中或已報廢的車輛不能新增掛載。");
    const bindingRef = doc(db, "orgs", orgId, "vehicleBindings", vehicleId);
    const current = await tx.get(bindingRef);
    const data = current.exists() ? current.data() : {};
    if (expectedVersion == null || expectedVersion !== vehicleBindingVersion(data)) throw new Error("掛載關係已變更，請關閉後重新開啟再操作。");
    const previousBinding = { trailerIds: bindingIds(data.trailerIds), containerIds: bindingIds(data.containerIds) };
    const affected = [...new Set([...previousBinding.trailerIds, ...previousBinding.containerIds, ...desired])];
    const assets = new Map();
    for (const id of affected) {
      const ref = doc(db, "orgs", orgId, "vehicles", id);
      const snapshot = await tx.get(ref);
      const asset = snapshot.exists() ? snapshot.data() : null;
      const alreadyHere = text(asset?.currentVehicleId) === vehicleId;
      if (desired.includes(id)) {
        const expectedKind = nextBinding.trailerIds.includes(id) ? "trailer" : "container";
        const status = normalizeAssetStatus(asset?.status);
        if (!asset || kindOf(asset) !== expectedKind || ["MAINTENANCE", "RETIRED"].includes(status) || (!alreadyHere && status !== "PARKED")) {
          throw new Error(`資產 ${id} 不存在、種類不符，或目前不是停放中可掛載狀態。`);
        }
      }
      if (asset?.currentVehicleId && asset.currentVehicleId !== vehicleId) throw new Error(`資產 ${id} 已掛載其他車輛，未修改任何資料。`);
      if (asset?.currentDriverId && !alreadyHere && desired.includes(id)) throw new Error(`資產 ${id} 已指派給成員，未修改任何資料。`);
      assets.set(id, { ref, asset: asset || {} });
    }
    const items = tx.getSummary();
    if (!items[vehicleId]) throw new Error("找不到車輛摘要，請先修復摘要。");
    for (const [otherId, item] of Object.entries(items)) {
      if (otherId === vehicleId || item.assetKind !== "vehicle") continue;
      if ([...(item.trailerIds || []), ...(item.containerIds || [])].some(id => desired.includes(id))) throw new Error(`資產已掛載車輛 ${otherId}，請先解除。`);
    }
    const changed = JSON.stringify(previousBinding) !== JSON.stringify(nextBinding);
    const vehicleNextStatus = vehicleStatus;
    if (changed) {
      tx.set(bindingRef, { ...nextBinding, revision: (data.revision || 0) + 1, updatedAt: serverTimestamp(), updatedByUid: actorUid });
      for (const [id, { ref, asset }] of assets) {
        const attached = desired.includes(id);
        const previousStatus = normalizeAssetStatus(asset.status);
        const nextStatus = attached ? "IN_USE" : (previousStatus === "IN_USE" ? "PARKED" : previousStatus);
        tx.set(ref, { currentVehicleId: attached ? vehicleId : null, status: nextStatus, updatedAt: serverTimestamp(), updatedByUid: actorUid }, { merge: true });
        if (items[id]) items[id] = { ...items[id], status: nextStatus, currentVehicleId: attached ? vehicleId : "" };
      }
      tx.set(vehicleRef, { status: vehicleNextStatus, updatedAt: serverTimestamp(), updatedByUid: actorUid }, { merge: true });
      items[vehicleId] = { ...items[vehicleId], status: vehicleNextStatus,
        trailerIds: nextBinding.trailerIds, containerIds: nextBinding.containerIds };
      tx.setVehicleBindingSummary(vehicleId, nextBinding);
      tx.setSummary(items);
      tx.set(eventRef, { type: "VEHICLE_BINDINGS_UPDATED", eventType: desired.length ? "VEHICLE_ASSETS_CHANGED" : "VEHICLE_ASSETS_CLEARED",
        vehicleId, assetIds: [vehicleId, ...affected], previousBinding, nextBinding, actorUid, actorName, actorRole, createdAt: serverTimestamp() });
    }
    return { ...nextBinding, changed, vehicleStatus: vehicleNextStatus,
      statuses: Object.fromEntries([...assets].map(([id, { asset }]) => [id,
        desired.includes(id) ? "IN_USE" : (normalizeAssetStatus(asset.status) === "IN_USE" ? "PARKED" : normalizeAssetStatus(asset.status))])) };
  });
}
